import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/database/userService';
import { User } from '../types/auth';
import {
  Users, GraduationCap, BookOpen, Shield, Clock, AlertTriangle,
  RefreshCw, TrendingUp, Database, CheckCircle,
} from 'lucide-react';

const SQLiteDatabasePage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [pendingTeachers, setPendingTeachers] = useState<User[]>([]);
  const [domainCounts, setDomainCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statsData, usersData, teacherData, allData] = await Promise.all([
        userService.getSystemStats(),
        userService.getAllUsers({ limit: 10 }),
        userService.getAllUsers({ role: 'teacher', limit: 100 }),
        userService.getAllUsers({ limit: 500 }),
      ]);

      setStats(statsData);
      setRecentUsers(usersData.users);

      const pending = teacherData.users.filter(u => {
        const info = (u as any).teacherInfo || (u as any).teacher_info;
        return !info?.isApproved;
      });
      setPendingTeachers(pending);

      const counts: Record<string, number> = {};
      allData.users.forEach(u => {
        const domain = (u as any).source_domain || 'unknown';
        counts[domain] = (counts[domain] || 0) + 1;
      });
      setDomainCounts(counts);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  const domainConfig: Record<string, { label: string; color: string; bg: string }> = {
    ebook:    { label: '📚 Ebook',    color: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200' },
    lab:      { label: '⚗️ Lab',      color: 'text-green-700',  bg: 'bg-green-50 border-green-200' },
    tronde:   { label: '📝 Trộn đề', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' },
    eng:      { label: '🌐 Eng',      color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
    vatli365: { label: '🏠 VatLi365', color: 'text-gray-700',   bg: 'bg-gray-100 border-gray-200' },
    unknown:  { label: '❓ Chưa rõ', color: 'text-gray-500',   bg: 'bg-gray-50 border-gray-100' },
  };

  const statCards = stats ? [
    { label: 'Tổng người dùng', value: stats.totalUsers, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Học sinh', value: stats.totalStudents, icon: GraduationCap, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Giáo viên', value: stats.totalTeachers, icon: BookOpen, color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Admin', value: stats.totalAdmins, icon: Shield, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Chờ duyệt', value: stats.pendingApprovals, icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { label: 'Hoạt động hôm nay', value: stats.activeToday, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Database className="w-8 h-8 text-emerald-600" />
              <h1 className="text-3xl font-bold text-gray-900">SQLite Database</h1>
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-medium rounded-full">
                Admin Panel
              </span>
            </div>
            <p className="text-gray-600">Tổng quan người dùng và phân bố theo nguồn đăng ký.</p>
          </div>
          <button
            onClick={loadData}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="text-sm">Làm mới</span>
          </button>
        </div>

        {isLoading && !stats ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {statCards.map((card) => (
                <div key={card.label} className={`${card.bg} rounded-xl p-4 border border-white shadow-sm`}>
                  <card.icon className={`w-6 h-6 ${card.color} mb-2`} />
                  <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-xs text-gray-600 mt-1">{card.label}</p>
                </div>
              ))}
            </div>

            {/* Domain Breakdown */}
            {Object.keys(domainCounts).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Phân bố theo nguồn đăng ký</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {Object.entries(domainCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([domain, count]) => {
                      const cfg = domainConfig[domain] || domainConfig.unknown;
                      return (
                        <div key={domain} className={`${cfg.bg} border rounded-xl p-4 text-center`}>
                          <p className={`text-3xl font-bold ${cfg.color}`}>{count}</p>
                          <p className={`text-xs font-medium mt-1 ${cfg.color}`}>{cfg.label}</p>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Recent Users + Pending Teachers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Users */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <span>Người dùng mới nhất</span>
                </h2>
                {recentUsers.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">Không có dữ liệu</p>
                ) : (
                  <div className="space-y-3">
                    {recentUsers.map(u => (
                      <div key={u.id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center shrink-0">
                            <span className="text-white text-xs font-bold">
                              {u.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                            <p className="text-xs text-gray-500 truncate">{u.email}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full shrink-0 ml-2 ${
                          u.role === 'admin'   ? 'bg-red-100 text-red-700' :
                          u.role === 'teacher' ? 'bg-orange-100 text-orange-700' :
                                                 'bg-blue-100 text-blue-700'
                        }`}>
                          {u.role === 'admin' ? 'Admin' : u.role === 'teacher' ? 'GV' : 'HS'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending Teacher Approvals */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <span>Giáo viên chờ duyệt</span>
                  {pendingTeachers.length > 0 && (
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-0.5 rounded-full">
                      {pendingTeachers.length}
                    </span>
                  )}
                </h2>
                {pendingTeachers.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Không có giáo viên chờ duyệt</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingTeachers.slice(0, 8).map(u => (
                      <div key={u.id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                            <span className="text-orange-600 text-xs font-bold">
                              {u.name?.charAt(0)?.toUpperCase() || 'T'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                            <p className="text-xs text-gray-500 truncate">{u.email}</p>
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full shrink-0 ml-2">
                          Chờ duyệt
                        </span>
                      </div>
                    ))}
                    {pendingTeachers.length > 8 && (
                      <p className="text-xs text-gray-400 text-center pt-1">
                        +{pendingTeachers.length - 8} người khác
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SQLiteDatabasePage;
